import {useParams} from "react-router-dom";
import {
  IonCol,
  IonRow,
  IonSelect,
  IonLabel,
  IonSelectOption,
  IonButton,
  IonInput,
  isPlatform,
} from "@ionic/react";
import {useContext, useEffect, useState} from "react";
import {v4 as uuidv4} from "uuid";

import {useQuery} from "@apollo/client";
import {GET_PACKAGE_PRICE_BY_ID} from "../../../graphql/queries/price.query";

import {AppButton, Breadcrumbs} from "../../../components";

import "./detail.style.css";
import {GET_MY_AGENCY} from "../../../graphql/queries/common.query";
import {AuthContext} from "../../../context/AuthContext";
import toast from "react-hot-toast";
import {isAuthenticated} from "../../../lib/utils/auth.utils";

const Details = () => {
  const {addToCart} = useContext(AuthContext);
  const {id} = useParams<any>();

  const [priceDetails, setPriceDetails] = useState<any>();
  const [agencyDetails, setAgencyDetails] = useState<any>();

  const [discountedPrice, setDiscountedPrice] = useState<number>();

  // Location selector
  const [location, setLocation] = useState<string | null>(null);
  // Truck/car selector
  const [isTruck, setIsTruck] = useState<boolean | null>(null);
  const [isVintage, setIsVintage] = useState<boolean | null>(null);
  const [kg, setKg] = useState<number | null>(null);
  const [kilowatt, setKilowatt] = useState<number | null>(null);
  // pec selector
  const [pecType, setPecType] = useState("");
  // personal/captial selector
  const [communicaType, setCommunicaType] = useState("");

  const [price, setPrice] = useState<any | null>(null);

  if (isAuthenticated()) {
    useQuery(GET_MY_AGENCY, {
      fetchPolicy: "cache-and-network",
      onCompleted: (data) => {
        setAgencyDetails(data.getMyAgency);
      },
    });
  }

  const {loading} = useQuery(GET_PACKAGE_PRICE_BY_ID, {
    variables: {
      id: parseInt(id),
    },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const result = data?.getPackagePriceBasedOnId?.data;
      setPriceDetails(result);

      if (result.packagePrice.length === 1) {
        setPrice(result.packagePrice[0]);
      }
    },
  });

  // Edge cases for different services
  const hasLocationSelector = [
    "foreignLicenseConversion",
    "licenseRenewal",
    "nauticalLicenseRenewal",
  ].includes(priceDetails?.serviceType);

  const hasTruckCarSelector = [
    "foreignVehicleRegistration",
    "motorVehicleOwnershipTransfer",
  ].includes(priceDetails?.serviceType);

  const hasAdminUniqueSelector = ["pec", "localUnitCommunication"].includes(
    priceDetails?.serviceType
  );

  // Calculating prices for different services based on selected options and inputs
  const calculateServicePrice = () => {
    // Services with only 1 pricing value
    if (priceDetails?.packagePrice.length === 1) {
      setPrice(priceDetails?.packagePrice[0]);
      return;
    }
    // Services with pricing value based on location
    if (hasLocationSelector) {
      const priceListValue = priceDetails?.packagePrice.find(
        (price: any) => price.location === location
      );
      setPrice(priceListValue);
    }
    // Services with pricing value based on if it is a truck/car or vintage
    if (hasTruckCarSelector) {
      if (isTruck) {
        const priceListValue = priceDetails?.packagePrice.find(
          (price: any) =>
            price.tab === "kg" &&
            kg &&
            kg >= price.range?.min &&
            kg <= price.range?.max
        );
        setPrice(priceListValue);
        return;
      }
      if (isVintage) {
        const priceListValue = priceDetails?.packagePrice.find((price: any) =>
          price.tab.startsWith("Auto d")
        );
        setPrice(priceListValue);
        return;
      }
      const priceListValue = priceDetails?.packagePrice.find(
        (price: any) =>
          price.tab === "kilowatt" &&
          kilowatt &&
          kilowatt === parseInt(price.category)
      );
      setPrice(priceListValue);
      return;
    }
    // Unique administator service where we need to get the price based on input values
    if (hasAdminUniqueSelector) {
      if (priceDetails?.serviceType === "pec") {
        const priceListValue = priceDetails?.packagePrice.find(
          (price: any) => price.tab === pecType
        );
        setPrice(priceListValue);
        return;
      } else {
        const priceListValue = priceDetails?.packagePrice.find(
          (price: any) => price.tab === communicaType
        );
        setPrice(priceListValue);
        return;
      }
    }
  };

  useEffect(() => {
    calculateServicePrice();
  }, [pecType, communicaType]);

  useEffect(() => {
    if (!price) return;

    if (!agencyDetails) {
      setDiscountedPrice(price.basicPrice);
      return;
    }

    switch (agencyDetails.client?.discount) {
      case "gold":
        setDiscountedPrice(price.goldPrice);
        return;
      case "premium":
        setDiscountedPrice(price.premiumPrice);
        return;
      case "base":
      default:
        setDiscountedPrice(price.basicPrice);
        return;
    }
  }, [price, agencyDetails]);

  // Just a check to disable the calculate button based on various cases for the services
  const cannotCalculatePrice = () => {
    if (hasLocationSelector) {
      if (!location) return true;
      if (priceDetails?.serviceType !== "nauticalLicenseRenewal") {
        if (!location) return true;
      }
    }
    if (hasTruckCarSelector) {
      if (isTruck === null) return true;
      if (isTruck && !kg) return true;
      if (!isTruck) {
        if (isVintage === null) return true;
        if (!isVintage && !kilowatt) return true;
      }
    }
    return false;
  };

  // service name and category name for breadcrumb
  const serviceName = priceDetails?.name;

  const categoryName =
    priceDetails?.service === "auto" ? "auto" : "amministrative";

  const addServiceToCart = (e: any) => {
    if (!discountedPrice) {
      toast.error("Prezzo non disponibile.");
      return;
    }
    if (!priceDetails.serviceItem) {
      toast.error("Nessun servizio trovato.");
      return;
    }
    if (!priceDetails.serviceItem.isPublic) {
      toast.error("Servizio non disponibile");
      return;
    }

    e.persist();
    isPlatform("desktop")
      ? //@ts-ignore
        document.getElementById("cartRef").click()
      : //@ts-ignore
        document.getElementById("cartMobileRef").click();

    addToCart(
      priceDetails.serviceItem.id,
      priceDetails.serviceItem.name,
      uuidv4()
    );
  };

  const breadcrumbEntries = [
    {
      title: `${
        serviceName ? serviceName + " - " : ""
      } Pratiche ${categoryName}`,
    },
  ];

  // Link on main website
  const getRedirectLink = () => {
    const redirectLink = process.env.REACT_APP_APPOINTMENT_URL || "";
    if (
      ["licenseRenewal", "foreignLicenseConversion"].includes(
        priceDetails?.serviceType
      )
    ) {
      return redirectLink;
    } else if (
      [
        "vehicleOwnershipTransfer",
        "motorVehicleOwnershipTransfer",
        "foreignVehicleRegistration",
      ].includes(priceDetails?.serviceType)
    ) {
      return redirectLink;
    }
    return redirectLink;
  };

  return (
    <div className="app-container">
      <Breadcrumbs items={breadcrumbEntries} backButton />
      {loading ? (
        <></>
      ) : priceDetails?.packagePrice.length === 1 || hasAdminUniqueSelector ? (
        <div className="order-page-wrapper order-page-wrapper-details">
          <IonRow className="height-full">
            <IonCol className="order-no-input-section">
              <div>
                <h1 className="details-title">{serviceName}</h1>
                <hr />

                {priceDetails?.serviceType === "pec" && (
                  <div className="details-option">
                    <IonLabel>
                      Seleziona una delle opzioni per scoprire il prezzo
                    </IonLabel>
                    <IonSelect
                      value={pecType}
                      onIonChange={(e) => {
                        setPecType(e.detail.value);
                      }}
                      interface="alert"
                      className="detail-selector"
                    >
                      <IonSelectOption value="bronze" key="bronze">
                        Bronze
                      </IonSelectOption>
                      <IonSelectOption value="silver" key="silver">
                        Silver
                      </IonSelectOption>
                      <IonSelectOption value="gold" key="gold">
                        Gold
                      </IonSelectOption>
                    </IonSelect>
                  </div>
                )}

                {priceDetails?.serviceType === "localUnitCommunication" && (
                  <div className="details-option">
                    <IonLabel>
                      Indicare se riguarda a un capitale o a una persona per
                      sapere il prezzo
                    </IonLabel>
                    <IonSelect
                      value={communicaType}
                      onIonChange={(e) => {
                        setCommunicaType(e.detail.value);
                      }}
                      interface="alert"
                      className="detail-selector"
                    >
                      <IonSelectOption value="individual" key="individual">
                        Persona
                      </IonSelectOption>
                      <IonSelectOption value="agency" key="agency">
                        Capitale
                      </IonSelectOption>
                    </IonSelect>
                  </div>
                )}

                <div className="price-group" style={{maxWidth: "400px"}}>
                  <span className="price-group-label">da</span>
                  <div className="price-item">
                    {discountedPrice ? <span>€ {discountedPrice}</span> : "-"}
                  </div>
                </div>

                {/* <p>{priceDetails.serviceItem?.description}</p> */}
              </div>
              <div className="detail-option-add-to-cart">
                <AppButton
                  block
                  buttonType="success"
                  className="add-to-cart-button"
                  onClick={addServiceToCart}
                >
                  Aggiungi al carrello
                </AppButton>
              </div>
            </IonCol>

            <IonCol
              sizeSm="12"
              offset-lg="1"
              sizeMd="12"
              sizeLg="6"
              sizeXs="12"
              className="order-extra-details"
            >
              <div className="order-extra-container">
                {/* {priceDetails?.serviceItem?.description && (
                  <p>{priceDetails?.serviceItem?.description}</p>
                )} */}

                {priceDetails?.note && (
                  <div>
                    <h4
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#333",
                        marginBottom: "8px",
                        textAlign: "left",
                      }}
                    >
                      <strong>Nota</strong>
                    </h4>
                    <p
                      style={{
                        whiteSpace: "pre-line",
                        textAlign: "left",
                        padding: "15px",
                        background: "#f9f9f9",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        color: "#555",
                        fontSize: "14px",
                        lineHeight: "1.6",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      {priceDetails?.note || ""}
                    </p>
                  </div>
                )}
              </div>
            </IonCol>
          </IonRow>
        </div>
      ) : (
        <div className="order-page-wrapper order-page-wrapper-details">
          <IonRow className="height-full">
            <IonCol
              sizeSm="12"
              sizeMd="12"
              sizeLg="5"
              sizeXs="12"
              className="order-input-section"
            >
              <h1 className="details-title">{serviceName}</h1>

              <hr />

              <div>
                {hasLocationSelector && (
                  <div>
                    <div className="details-option">
                      <IonLabel>
                        {"Sede in cui effettuare il servizio"}
                      </IonLabel>
                      <IonSelect
                        value={location}
                        onIonChange={(e) => {
                          setLocation(e.detail.value);
                          setPrice(null);
                        }}
                        interface="alert"
                        className="detail-selector"
                        interfaceOptions={{
                          cssClass: "detail-selector",
                        }}
                      >
                        <IonSelectOption value="milano" defaultChecked>
                          Milano, Via Rembrandt 22
                        </IonSelectOption>
                        <IonSelectOption value="monza">
                          Monza, Viale Vittorio Veneto 5, 20090 Monza
                        </IonSelectOption>
                      </IonSelect>
                    </div>
                  </div>
                )}
                {hasTruckCarSelector && (
                  <div className="details-option">
                    <IonLabel>È un autocarro?</IonLabel>
                    <IonSelect
                      value={isTruck}
                      onIonChange={(e) => {
                        setIsTruck(e.detail.value);
                        setPrice(null);
                      }}
                      interface="alert"
                      className="detail-selector"
                    >
                      <IonSelectOption value={true} key="yes">
                        sì
                      </IonSelectOption>

                      <IonSelectOption value={false} key="no">
                        no
                      </IonSelectOption>
                    </IonSelect>

                    {isTruck === true ? (
                      <div className="details-option">
                        <IonLabel>Inserire la capacità in kg</IonLabel>
                        <IonInput
                          value={kg}
                          placeholder="Inserisci valore"
                          inputMode="numeric"
                          type="number"
                          min="1"
                          max="8000"
                          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          onIonChange={(e) => setKg(parseInt(e.detail.value!))}
                          className="detail-selector"
                        />
                      </div>
                    ) : (
                      <>
                        {isTruck === false && (
                          <div className="details-option">
                            <IonLabel>È d’epoca?</IonLabel>
                            <IonSelect
                              value={isVintage}
                              onIonChange={(e) => {
                                setIsVintage(e.detail.value);
                                setPrice(null);
                              }}
                              interface="alert"
                              className="detail-selector"
                            >
                              <IonSelectOption value={true} key="yes">
                                sì
                              </IonSelectOption>

                              <IonSelectOption value={false} key="no">
                                no
                              </IonSelectOption>
                            </IonSelect>
                          </div>
                        )}

                        {isVintage === false && (
                          <div className="details-option">
                            <IonLabel>
                              Inserire I Kilowatts del veicolo
                            </IonLabel>
                            <IonInput
                              value={kilowatt}
                              placeholder="Inserisci valore"
                              inputMode="numeric"
                              type="number"
                              min="53"
                              max="150"
                              onIonChange={(e) =>
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                setKilowatt(parseInt(e.detail.value!))
                              }
                              className="detail-selector"
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="details-option">
                  <IonButton
                    disabled={cannotCalculatePrice()}
                    expand="full"
                    shape="round"
                    className="details-button"
                    onClick={() => calculateServicePrice()}
                  >
                    Calcola
                  </IonButton>
                </div>

                {price && (
                  <div className="price-group">
                    <span className="price-group-label">da</span>
                    <div className="price-item">
                      <strong>€ {discountedPrice}</strong>
                    </div>
                  </div>
                )}
              </div>
            </IonCol>
            <IonCol
              sizeSm="12"
              offset-lg="1"
              sizeMd="12"
              sizeLg="6"
              sizeXs="12"
              className="order-extra-details"
            >
              <div className="order-extra-container">
                {/* {priceDetails?.serviceItem?.description && (
                  <p>{priceDetails?.serviceItem?.description}</p>
                )} */}

                <div>
                  <h4>
                    <strong>
                      Per fare questa pratica devi venirci a trovare
                    </strong>
                  </h4>
                </div>

                <div className="order-extra-details-item">
                  <IonButton
                    expand="full"
                    shape="round"
                    className="details-button"
                    href={getRedirectLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Prenota appuntamento
                  </IonButton>
                </div>

                <div>
                  <hr />

                  <h4
                    style={{
                      fontSize: "18px",
                      fontWeight: "bold",
                      color: "#333",
                      marginBottom: "8px",
                      textAlign: "left",
                    }}
                  >
                    <strong>Nota</strong>
                  </h4>
                  <p
                    style={{
                      whiteSpace: "pre-line",
                      textAlign: "left",
                      padding: "15px",
                      background: "#f9f9f9",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      color: "#555",
                      fontSize: "14px",
                      lineHeight: "1.6",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    {priceDetails?.note || "Nessuna nota disponibile."}
                  </p>
                </div>
              </div>
            </IonCol>
          </IonRow>
        </div>
      )}
    </div>
  );
};

export default Details;
